import './PlayerSheet.css';

/** A player the sheet can act on (resolved from either roster). */
export interface SheetPlayer {
  id: string;
  name: string;
  isHost?: boolean;
}

interface PlayerSheetProps {
  player: SheetPlayer;
  /** Within earshot of the local player (drives the badge + Call vs Chat rule). */
  isNearby: boolean;
  /** P3 flag: show "Invite to room" only when room invites have shipped. */
  invitesEnabled?: boolean;
  /** Start (or enter) a scoped chat with this player. */
  onChat: () => void;
  /** Reveal the local player's gesture bar. */
  onGesture: () => void;
  /** Approval-gated phone call (only offered when out of earshot). */
  onCall: () => void;
  /** P3 room invite. */
  onInvite?: () => void;
  /** Open the full profile card (PlayerProfileOverlay). */
  onFullProfile: () => void;
  onClose: () => void;
}

/**
 * P1 — the one interaction surface for a tapped player. Reached two ways
 * (both route through App.handleOpenProfile for HUMAN ids):
 *   • tapping a 3D avatar (Unity `openProfile` event), or
 *   • tapping a header pill / the expanded `👥` roster chip.
 *
 * Mobile: a bottom sheet (thumb-zone actions). Desktop: a right-side card.
 * NPCs never reach here — their ids (`npc_*`) still open PlayerProfileOverlay,
 * keeping the character-guided-profiling chat intact.
 *
 * The sheet holds NO chat/private-talk state itself — the actions delegate up
 * to App so the shipped consent handshake and CallPanel wires are reused
 * verbatim (design doc P1 §2.2).
 */
export function PlayerSheet({
  player,
  isNearby,
  invitesEnabled = false,
  onChat,
  onGesture,
  onCall,
  onInvite,
  onFullProfile,
  onClose,
}: PlayerSheetProps) {
  return (
    <div className="player-sheet-backdrop" onClick={onClose}>
      <div
        className="player-sheet"
        role="dialog"
        aria-label={`Actions for ${player.name}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="player-sheet-grabber" aria-hidden="true" />
        <button className="player-sheet-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        {/* 1. Identity row */}
        <div className="player-sheet-identity">
          <div className="player-sheet-avatar" aria-hidden="true">
            {player.name.charAt(0).toUpperCase()}
          </div>
          <div className="player-sheet-identity-text">
            <h3 className="player-sheet-name">{player.name}</h3>
            <div className="player-sheet-badges">
              <span className={`player-sheet-earshot ${isNearby ? 'near' : 'far'}`}>
                {isNearby ? '🗣 in earshot' : '📵 too far'}
              </span>
              {player.isHost && <span className="player-sheet-host">host</span>}
            </div>
          </div>
        </div>

        {/* 2. Action row (thumb zone) */}
        <div className="player-sheet-actions">
          <button className="player-sheet-action primary" onClick={onChat}>
            <span className="player-sheet-action-icon" aria-hidden="true">💬</span>
            <span className="player-sheet-action-label">Chat</span>
          </button>
          <button className="player-sheet-action" onClick={onGesture}>
            <span className="player-sheet-action-icon" aria-hidden="true">👋</span>
            <span className="player-sheet-action-label">Gesture</span>
          </button>
          {!isNearby && (
            <button className="player-sheet-action" onClick={onCall}>
              <span className="player-sheet-action-icon" aria-hidden="true">📞</span>
              <span className="player-sheet-action-label">Call</span>
            </button>
          )}
          {invitesEnabled && onInvite && (
            <button className="player-sheet-action" onClick={onInvite}>
              <span className="player-sheet-action-icon" aria-hidden="true">🚪</span>
              <span className="player-sheet-action-label">Invite</span>
            </button>
          )}
          <button className="player-sheet-action" onClick={onFullProfile}>
            <span className="player-sheet-action-icon" aria-hidden="true">👤</span>
            <span className="player-sheet-action-label">Profile</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default PlayerSheet;
